import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Divider,
  Grid,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material';
import useAPICall from '../../hooks/useAPICall';
import { API_CONFIG } from '../../config/api.config';
import GlobalMaterialTable from '../../components/common/GlobalMaterialTable';
import PageBreadcrumbs from '../../components/common/PageBreadcrumbs';
import {
  MRT_FullScreenToggleButton as MrtFullScreenToggleButton,
  MRT_ShowHideColumnsButton as MrtShowHideColumnsButton,
  MRT_ToggleDensePaddingButton as MrtToggleDensePaddingButton,
  MRT_ToggleFiltersButton as MrtToggleFiltersButton,
  MRT_ToggleGlobalFilterButton as MrtToggleGlobalFilterButton,
} from 'material-react-table';

const getBatchRows = (data) => {
  if (Array.isArray(data)) {
    return data[0] || null;
  }
  if (Array.isArray(data?.data)) {
    return data.data[0] || null;
  }
  return data || null;
};

const HCPNewRequest = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const batchId = searchParams.get('batchId');
  const { loading, error, data, execute } = useAPICall();
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  useEffect(() => {
    const loadBatch = async () => {
      if (!batchId) return;
      try {
        await execute(API_CONFIG.workflow.getHcpBatchById, null, {
          params: { batchId },
          showErrorToast: false,
          errorMessage: 'Unable to load HCP batch requests.',
        });
      } catch {
      }
    };

    loadBatch();
  }, [batchId, execute]);

  const batch = useMemo(() => getBatchRows(data), [data]);
  const rows = useMemo(() => (Array.isArray(batch?.requests) ? batch.requests : []), [batch]);

  const openMerge = (row) => {
    const requestId = row?.requestId || row?.request_id || row?.id;
    if (!batchId || !requestId) return;
    navigate(`/hcp-merge?batchId=${batchId}&reqId=${requestId}`);
  };

  const getIssue = (row) => row?.requestIssueType || row?.request?.requestIssueType || 'POTENTIAL_MATCH';
  const getChangeSource = (row) => row?.requestChangeSource || row?.request?.requestChangeSource || row?.source || row?.request?.source || '-';
  const getUpdatedBy = (row) => row?.requestUpdatedBy || row?.request?.requestUpdatedBy || '-';

  const pageHeaderSx = {
    px: 2,
    py: 1.5,
    borderBottom: '1px solid #E5E7EB',
    borderLeft: '4px solid #1E88E5',
    display: 'flex',
    background: "#fbfdfe",
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 2,
    mb: 3,
  };

  return (
    <Box sx={{ maxWidth: '100vw', pb: 6, mt: 1, px: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <PageBreadcrumbs items={[
          { label: 'Open Batches', path: '/inbox/system' },
          { label: `Requests in Batch ID ${batchId || '-'}` }
        ]} />
      </Box>

      <Paper elevation={0} sx={{
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        backgroundColor: '#fff',
        overflow: 'hidden'
      }}>
        <Box sx={pageHeaderSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E293B', mb: 0.25, lineHeight: 1.2 }}>
                HCP Batch Requests
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                Review requests in the selected HCP inbox batch.
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{  }}>
          {loading ? (
            <Grid container spacing={2}>
              {Array.from({ length: 6 }).map((_, i) => (
                <Grid item xs={12} md={3} key={i}>
                  <Skeleton variant="text" width="80%" height={24} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <>
              <Grid container spacing={2} sx={{ px:2, pb: 2,borderBottom: "1px solid #eaeaea" }}>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2"><strong>Batch ID:</strong> {batch?.batchId || batchId || '-'}</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2"><strong>Status:</strong> {batch?.batchStatus || '-'}</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2"><strong>Created:</strong> {batch?.batchCreatedTime || '-'}</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2"><strong>Request Count:</strong> {rows.length}</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2"><strong>Last Updated:</strong> {batch?.batchLastUpdatedTime || '-'}</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2"><strong>Last Updated By:</strong> {batch?.batchLastUpdatedBy || '-'}</Typography>
                </Grid>
              </Grid>

              <Paper sx={{ boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderRadius: 2, overflow: 'hidden' }}>
                <GlobalMaterialTable
                  tableTitle="Batch Requests"
                  tableCount={rows.length}
                  columns={[
                  { header: 'Request ID', id: 'requestId', accessorFn: (row, index) => row.requestId || row.request_id || row.id || index },
                  { header: 'MDM ID', id: 'mdmId', accessorFn: (row) => row.requestMdmId || row.mdmId || row.request?.requestMdmId || '-' },
                  { header: 'Created Time', id: 'createdTime', accessorFn: (row) => row.requestCreatedTime || row.createdTime || row.request?.requestCreatedTime || '-' },
                  { header: 'Last Updated Time', id: 'updatedTime', accessorFn: (row) => row.requestLastUpdatedTime || row.updatedTime || row.request?.requestLastUpdatedTime || '-' },
                  { header: 'Status', id: 'status', accessorFn: (row) => row.requestStatus || row.status || '-' },
                  { header: 'Issue', id: 'issue', accessorFn: (row) => getIssue(row) },
                  { header: 'Change Source', id: 'changeSource', accessorFn: (row) => getChangeSource(row) },
                  { header: 'Updated By', id: 'updatedBy', accessorFn: (row) => getUpdatedBy(row) },
                ]}
                data={rows}
                emptyMessage="No requests found for this batch"
                enablePagination
                enableBottomToolbar
                manualPagination={false}
                page={pagination.pageIndex + 1}
                pageSize={pagination.pageSize}
                onPageChange={(p) => setPagination(prev => ({ ...prev, pageIndex: p - 1 }))}
                onPageSizeChange={(ps) => setPagination(prev => ({ ...prev, pageSize: ps }))}
                enableTopToolbar
                renderToolbarInternalActions={({ table }) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <MrtToggleGlobalFilterButton table={table} />
                    <MrtToggleFiltersButton table={table} />
                    <MrtShowHideColumnsButton table={table} />
                    <MrtToggleDensePaddingButton table={table} />
                    <MrtFullScreenToggleButton table={table} />
                  </Box>
                )}
                muiSearchTextFieldProps={{ placeholder: 'Search batch requests...' }}
                getRowId={(row, index) => String(row.requestId || row.request_id || row.id || index)}
                muiTableBodyRowProps={({ row }) => ({
                  onDoubleClick: () => openMerge(row.original),
                  sx: { cursor: 'pointer' },
                })}
                muiTablePaperProps={{
                  elevation: 0,
                  OuterBorder: "none",
                  sx: {
                    border: 'none !important',
                    boxShadow: 'none !important',
                  },
                }}
              />
              </Paper>
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default HCPNewRequest;
